/** @jsx jsx */
import { jsx, Flex, Box, useThemeUI } from 'theme-ui';
import React, { useState, useCallback, Component, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AsyncSelect from 'react-select/async';
import Button from '../Button';
import { DateInput } from '../DateField';
import Widget from './Widget';
import { autocompleteBusLocation } from '../../utils/api';
import { formatDate } from '../../utils/formatters';

import Spinner from '../Spinner';
import { CalendarIcon, CaretIcon, ArrowIcon } from '../Icon';
import moment from 'moment';
import FavoriteTripDropdownButton from './FavoriteTripDropdown';
import Option from './ReactSelectCustomOption';

export default ({ showFavoriteTrips = true, frontPage = false, ...props }) => {
  const { i18n } = useTranslation();
  const language = i18n.language;
  const hasUser = useSelector((state) => !!(state.session && state.session.user));
  const { theme } = useThemeUI();

  const [departurePlace, setDeparturePlace] = useState();
  const selectDeparturePlace = useCallback((v) => {
    if (!v) {
      return;
    }
    setDeparturePlace(v);
    destinationRef.current?.focus();
    return `${v.name} (${v.region})`;
  }, []);

  const [arrivalPlace, setArrivalPlace] = useState();
  const selectArrivalPlace = useCallback((v) => {
    if (!v) {
      return;
    }
    setArrivalPlace(v);
    calendarRef.current?.focus();
    return `${v.name} (${v.region})`;
  }, []);

  const [departureDate, setDepartureDate] = useState();
  const selectDate = useCallback(
    (v) => {
      setDepartureDate(v);
    },
    [setDepartureDate]
  );

  const redirectToTicketService = useCallback(() => {
    let url = process.env.GATSBY_TICKET_API_URL + '/timetable?';
    url += `lang=${language}`;
    url += `&departurePlaceId=${departurePlace?.id || ''}`;
    url += `&arrivalPlaceId=${arrivalPlace?.id || ''}`;
    url += `&departureDate=${formatDate(departureDate)}`;
    // url += `&returnDate=${returnDate || ''}`;
    window.location.assign(url);
  });

  const { t: translate } = useTranslation();
  const selectStyles = {
    container: (base) => ({
      ...base,
      position: 'static',
      width: '100%',
      left: 0,
    }),
    control: (base) => ({
      ...base,
      position: 'static',
      border: 'none',
      boxShadow: 'none',
      cursor: 'text',
    }),
    indicatorsContainer: (base) => ({ display: 'none' }),
    singleValue: (base, { selectProps: { alignRight } }) => ({
      ...base,
      textAlign: alignRight ? 'right' : 'left',
    }),
    placeholder: (base, { selectProps: { alignRight } }) => ({
      ...base,
      textAlign: alignRight ? 'right' : 'left',
      color: theme.colors.gray,
    }),
    menu: (base) => ({
      ...base,
      left: 0,
      paddingTop: 8,
      paddingBottom: 8,
    }),
    option: (base, state) => ({
      ...base,
      color: '#001E60',
      backgroundColor: state.isFocused ? '#E5E8EF' : 'none',
      ':hover': { backgroundColor: '#E5E8EF' },
    }),
  };

  const handleTripSelected = useCallback(
    (trip) => {
      if (!trip.departure || !trip.arrival) return;
      let url = process.env.GATSBY_TICKET_API_URL + '/timetable?';
      url += `lang=${language}`;
      url += `&departurePlaceId=${trip.departure.id || ''}`;
      url += `&arrivalPlaceId=${trip.arrival.id || ''}`;
      url += `&departureDate=${formatDate(departureDate)}`;
      // url += `&returnDate=${returnDate || ''}`;
      window.location.assign(url);
    },
    [language, departureDate]
  );

  const destinationRef = useRef(null);
  const calendarRef = useRef(null);

  return (
    <Widget
      title={translate('timeTableWidget.title')}
      action={hasUser && showFavoriteTrips && <FavoriteTripDropdownButton onTripSelected={handleTripSelected} />}
      role="region"
      aria-label={translate('timeTableWidget.title')}
      {...props}
    >
      <Box></Box>
      <Box>
        <Flex
          sx={{
            position: 'relative',
            bg: 'white',
            borderRadius: 1,
            height: 48,
            justifyContent: 'space-between',
            alignItems: 'center',
            color: theme.colors.gray,
          }}
        >
          <AsyncSelect
            components={{ Option }}
            inputId="departure-place-select"
            instanceId="departure-place"
            name="departurePlace"
            aria-label={
              translate('timeTableWidget.ariaFrom')
              // + (departurePlace ? ` ${departurePlace?.name} ${translate('timeTableWidget.selected')}` : '')
            }
            placeholder={translate('timeTableWidget.from')}
            styles={selectStyles}
            isClearable={true}
            loadOptions={(term) => autocompleteBusLocation(term, language)}
            getOptionLabel={(opt) => {
              return `${opt.name} (${opt.region})`;
            }}
            getOptionValue={(opt) => opt.id}
            isOptionSelected={(option) => departurePlace?.id === option.id}
            onChange={selectDeparturePlace}
            noOptionsMessage={({ inputValue }) =>
              inputValue ? translate('timeTableWidget.noResults') : translate('timeTableWidget.typeToSearch')
            }
            loadingMessage={() => (
              <Box sx={{ py: 2 }}>
                <Spinner />
              </Box>
            )}
            // openMenuOnFocus={true}
            tabSelectsValue={true}
          />
          <ArrowIcon
            aria-hidden={true}
            sx={{
              display: 'block',
              flex: 'none',
              px: 1,
              boxSizing: 'content-box',
            }}
          />
          <AsyncSelect
            components={{ Option }}
            inputId="arrival-place-select"
            instanceId="arrival-place"
            name="arrivalPlace"
            aria-label={
              translate('timeTableWidget.ariaTo')
              // + (arrivalPlace ? ` ${arrivalPlace?.name} ${translate('timeTableWidget.selected')}` : '')
            }
            placeholder={translate('timeTableWidget.to')}
            ref={destinationRef}
            styles={selectStyles}
            loadOptions={(term) => autocompleteBusLocation(term, language)}
            getOptionLabel={(opt) => {
              return `${opt.name} (${opt.region})`;
            }}
            getOptionValue={(opt) => opt.id}
            isOptionSelected={(option) => arrivalPlace?.id === option.id}
            onChange={selectArrivalPlace}
            noOptionsMessage={({ inputValue }) =>
              inputValue ? translate('timeTableWidget.noResults') : translate('timeTableWidget.typeToSearch')
            }
            alignRight={true}
            loadingMessage={() => (
              <Box sx={{ py: 2 }}>
                <Spinner />
              </Box>
            )}
            // openMenuOnFocus={true}
            tabSelectsValue={true}
          />
        </Flex>
        <Flex
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 3,
            flexWrap: 'wrap',
          }}
        >
          <DateInput inputRef={calendarRef} selectedDate={departureDate} onChange={selectDate} />
          <Button
            variant="secondary"
            onClick={redirectToTicketService}
            sx={{
              bg: frontPage ? 'newBlue' : undefined,
              color: frontPage ? 'white' : undefined,
              border: frontPage ? 0 : undefined,
            }}
          >
            {translate('timeTableWidget.search')}
          </Button>
        </Flex>
      </Box>
    </Widget>
  );
};
